import { ViewExtensionManagerIdentifier } from '@blocksuite/affine-ext-loader';
import {
  DefaultTheme,
  type FrameBlockModel,
  surfaceColor,
} from '@blocksuite/affine-model';
import {
  DocModeExtension,
  DocModeProvider,
  ThemeProvider,
  ViewportElementExtension,
} from '@blocksuite/affine-shared/services';
import { DisposableGroup } from '@blocksuite/global/disposable';
import { Bound, deserializeXYWH } from '@blocksuite/global/gfx';
import { WithDisposable } from '@blocksuite/global/lit';
import {
  BlockStdScope,
  type EditorHost,
  LifeCycleWatcher,
  ShadowlessElement,
} from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import { type ExtensionType, type Query, type Store } from '@blocksuite/store';
import { css, html, nothing, type PropertyValues } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import { styleMap } from 'lit/directives/style-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import debounce from 'lodash-es/debounce';
const DEFAULT_PREVIEW_CONTAINER_WIDTH = 280;
const DEFAULT_PREVIEW_CONTAINER_HEIGHT = 166;

const styles = css`
  .frame-preview-container {
    display: block;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    position: relative;
  }

  .frame-preview-surface-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    overflow: hidden;
  }

  .frame-preview-viewport {
    max-width: 100%;
    box-sizing: border-box;
    margin: 0 auto;
    position: relative;
    overflow: hidden;
    pointer-events: none;
    user-select: none;
  }

  .frame-preview-viewport
    > editor-host
    > affine-edgeless-root-preview
    > .edgeless-background {
    background-color: transparent;
    background-image: none;
  }

  .frame-preview-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: var(--streamlain-v2-layer-background-secondary);
    border-radius: 4px;
  }
`;

export const AFFINE_FRAME_PREVIEW = 'frame-preview';

export class FramePreview extends WithDisposable(ShadowlessElement) {
  static override styles = styles;

  private _intersectionObserver: IntersectionObserver | null = null;

  private _idleCallbackId: number | null = null;

  private _isInitialized = false;

  private readonly _clearFrameDisposables = () => {
    this._frameDisposables?.dispose();
    this._frameDisposables = null;
  };

  private readonly _docFilter: Query = {
    mode: 'loose',
    match: [
      {
        flavour: 'affine:frame',
        viewType: 'hidden',
      },
    ],
  };

  private _frameDisposables: DisposableGroup | null = null;

  private _previewDoc: Store | null = null;

  private _runtimePreviewExt: ExtensionType[] = [];

  private get _viewExtensionManager() {
    return this.std.get(ViewExtensionManagerIdentifier);
  }

  private get _previewSpec() {
    return this._viewExtensionManager.get('preview-edgeless');
  }

  private readonly _updateFrameViewportWH = () => {
    const [, , w, h] = deserializeXYWH(this.frame.xywh);

    let scale = 1;
    if (this.fillScreen) {
      scale = Math.max(this.surfaceWidth / w, this.surfaceHeight / h);
    } else {
      scale = Math.min(this.surfaceWidth / w, this.surfaceHeight / h);
    }

    this.frameViewportWH = {
      width: w * scale,
      height: h * scale,
    };
  };

  get _originalDoc() {
    return this.frame.store;
  }

  private _initPreviewDoc() {
    const doc = this._originalDoc.workspace.getDoc(this._originalDoc.id);
    this._previewDoc =
      doc?.getStore({ readonly: true, query: this._docFilter }) ?? null;
    this.disposables.add(() => {
      this._originalDoc.doc.removeStore({ query: this._docFilter });
    });
  }

  private _initSpec() {
    const refreshViewport = this._refreshViewport.bind(this);
    class FramePreviewWatcher extends LifeCycleWatcher {
      static override key = 'frame-preview-watcher';

      override mounted() {
        const std = this.std;
        const { view } = std;
        view.viewUpdated.subscribe(payload => {
          if (
            payload.type !== 'block' ||
            payload.method !== 'add' ||
            payload.view.model.flavour !== 'affine:page'
          ) {
            return;
          }
          const viewport = std.get(GfxControllerIdentifier).viewport;
          const subscription = viewport.sizeUpdated.subscribe(() => {
            subscription.unsubscribe();
            refreshViewport();
          });
        });
      }
    }

    const docModeService = this.std.get(DocModeProvider);
    this._runtimePreviewExt = [
      ViewportElementExtension('.frame-preview-viewport'),
      FramePreviewWatcher,
      DocModeExtension(docModeService),
    ];
  }

  private _refreshViewport() {
    const previewEditorHost = this.previewEditor;

    if (!previewEditorHost) return;

    const { viewport } = previewEditorHost.std.get(GfxControllerIdentifier);
    const frameBound = Bound.deserialize(this.frame.xywh);
    viewport.setViewportByBound(frameBound);
  }

  private _renderSurfaceContent() {
    const { width, height } = this.frameViewportWH;

    // StreamLain: Check snapshot cache (hook pattern - logic in separate module)
    const cached = (window as any).__streamlainFrameSnapshotHook?.getSnapshot?.(
      this.frame?.id
    );
    if (cached) {
      return html`<div
        class="frame-preview-surface-container"
        style=${styleMap({
          width: `${this.surfaceWidth}px`,
          height: `${this.surfaceHeight}px`,
        })}
      >
        ${unsafeHTML(cached)}
      </div>`;
    }

    // StreamLain: Get frame background for live preview (same as cached snapshot)
    const backgroundColor = this.std
      .get(ThemeProvider)
      .generateColorProperty(
        surfaceColor(this.frame.props.background, 'frame'),
        DefaultTheme.transparent
      );
    const hasBackground = backgroundColor && backgroundColor !== 'transparent';

    const _previewSpec = this._previewSpec.concat(this._runtimePreviewExt);
    return html`<div
      class="frame-preview-surface-container"
      style=${styleMap({
        width: `${this.surfaceWidth}px`,
        height: `${this.surfaceHeight}px`,
      })}
    >
      ${hasBackground
        ? html`<div
            style=${styleMap({
              background: backgroundColor,
              width: '100%',
              height: '100%',
              borderRadius: '2px',
            })}
          >
            <div
              class="frame-preview-viewport"
              style=${styleMap({
                width: `${width}px`,
                height: `${height}px`,
              })}
            >
              ${guard([this._previewDoc, this.frame], () => {
                if (!this._previewDoc || !this.frame) return nothing;
                return new BlockStdScope({
                  store: this._previewDoc,
                  extensions: _previewSpec,
                }).render();
              })}
            </div>
          </div>`
        : html`<div
            class="frame-preview-viewport"
            style=${styleMap({
              width: `${width}px`,
              height: `${height}px`,
            })}
          >
            ${guard([this._previewDoc, this.frame], () => {
              if (!this._previewDoc || !this.frame) return nothing;
              return new BlockStdScope({
                store: this._previewDoc,
                extensions: _previewSpec,
              }).render();
            })}
          </div>`}
    </div>`;
  }

  private _setFrameDisposables(frame: FrameBlockModel) {
    this._clearFrameDisposables();
    this._frameDisposables = new DisposableGroup();

    // StreamLain: Debounced invalidation handler (reused for frame + children)
    const invalidateAndUpdate = debounce(
      () => {
        this._updateFrameViewportWH();
        // Invalidate snapshot (hook pattern)
        (window as any).__streamlainFrameSnapshotHook?.invalidate?.(frame.id);
        // Trigger re-render so updated() will re-capture the frame
        (this as any).requestUpdate?.();
      },
      50,
      { leading: true, maxWait: 200 }
    );

    // 1. Frame props changed (position, size, background)
    this._frameDisposables.add(
      frame.propsUpdated.subscribe(invalidateAndUpdate)
    );

    // 2. StreamLain: Child element changes - O(1) lookup via _parentGroupId
    // When ANY block updates, check if it's inside this frame (direct or nested)
    // Helper to check if element is inside this frame
    const isElementInFrame = (id: string): boolean => {
      const surface = frame.surface;
      if (!surface) return false;

      const element =
        surface.getElementById(id) ?? surface.store.getModelById(id);
      if (!element) return false;

      // Direct child: O(1) via childElementIds map
      if (frame.hasChild(element as any)) return true;

      // Nested element: O(d) parent chain walk
      const parentChain = surface.getGroups(id);
      return parentChain.some(group => group.id === frame.id);
    };

    // 2a. Block changes (affine:paragraph, affine:list, etc.)
    this._frameDisposables.add(
      frame.store.slots.blockUpdated.subscribe(({ type, id }) => {
        if (id === frame.id) return;
        if (type !== 'update' && type !== 'add' && type !== 'delete') return;

        if (isElementInFrame(id)) {
          invalidateAndUpdate();
        }
      })
    );

    // 2b. Surface element changes (shapes, connectors, etc.)
    const surface = frame.surface;
    if (surface) {
      this._frameDisposables.add(
        surface.elementUpdated.subscribe(({ id }) => {
          if (id === frame.id) return;

          if (isElementInFrame(id)) {
            invalidateAndUpdate();
          }
        })
      );

      // Also listen for element add/remove
      this._frameDisposables.add(
        surface.elementAdded.subscribe(({ id }) => {
          if (isElementInFrame(id)) {
            invalidateAndUpdate();
          }
        })
      );

      this._frameDisposables.add(
        surface.elementRemoved.subscribe(() => {
          // Element was removed, can't check if it was in frame anymore
          // But if it was a child, the frame's childElementIds will update
          // which triggers propsUpdated (already handled above)
        })
      );
    }
  }

  private _initializePreview() {
    if (this._isInitialized) return;
    this._isInitialized = true;

    this._initSpec();
    this._initPreviewDoc();
    this._updateFrameViewportWH();
    this._setFrameDisposables(this.frame);
  }

  private _scheduleInitialization() {
    if (this._idleCallbackId !== null) return;

    // Use idle callback to avoid blocking main thread
    this._idleCallbackId = requestIdleCallback(
      () => {
        this._idleCallbackId = null;
        this._initializePreview();
      },
      { timeout: 1000 }
    );
  }

  private _setupIntersectionObserver() {
    this._intersectionObserver = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry.isIntersecting && !this._isVisible) {
          this._isVisible = true;
          this._scheduleInitialization();
        }
      },
      {
        threshold: 0,
        // Preload frames 50% before they become visible
        rootMargin: '50% 0px 50% 0px',
      }
    );
    this._intersectionObserver.observe(this);
  }

  // StreamLain: Handler for snapshot loaded event (triggers re-render)
  private readonly _onSnapshotLoaded = (e: Event) => {
    const frameId = (e as CustomEvent).detail?.frameId;
    if (frameId === this.frame?.id) (this as any).requestUpdate?.();
  };

  override connectedCallback() {
    super.connectedCallback();
    this._setupIntersectionObserver();
    // StreamLain: Listen for snapshot loaded from IndexedDB
    window.addEventListener(
      'streamlain-frame-snapshot-loaded',
      this._onSnapshotLoaded
    );
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(
      'streamlain-frame-snapshot-loaded',
      this._onSnapshotLoaded
    );
    this._intersectionObserver?.disconnect();
    this._intersectionObserver = null;
    if (this._idleCallbackId !== null) {
      cancelIdleCallback(this._idleCallbackId);
      this._idleCallbackId = null;
    }
    this._clearFrameDisposables();
  }

  private _renderPlaceholder() {
    return html`<div
      class="frame-preview-surface-container"
      style=${styleMap({
        width: `${this.surfaceWidth}px`,
        height: `${this.surfaceHeight}px`,
      })}
    >
      <div class="frame-preview-placeholder"></div>
    </div>`;
  }

  override render() {
    const { frame } = this;
    const noContent = !frame || !frame.xywh;

    if (noContent) {
      return html`<div class="frame-preview-container">${nothing}</div>`;
    }

    if (!this._isVisible) {
      return html`<div class="frame-preview-container">
        ${this._renderPlaceholder()}
      </div>`;
    }

    return html`<div class="frame-preview-container">
      ${this._renderSurfaceContent()}
    </div>`;
  }

  override updated(_changedProperties: PropertyValues) {
    if (_changedProperties.has('frame') && this._isInitialized) {
      this._setFrameDisposables(this.frame);
    }
    if (_changedProperties.has('frameViewportWH') && this._isInitialized) {
      this._refreshViewport();
    }
    // StreamLain: Capture snapshot after render (hook pattern)
    if (this._isInitialized && this._isVisible && this.frame) {
      const el = (this as unknown as Element).querySelector?.(
        '.frame-preview-viewport'
      );
      if (el) {
        // Get frame background color from model (Shadow DOM doesn't clone)
        const backgroundColor = this.std
          .get(ThemeProvider)
          .generateColorProperty(
            surfaceColor(this.frame.props.background, 'frame'),
            DefaultTheme.transparent
          );
        (window as any).__streamlainFrameSnapshotHook?.capture?.(
          this.frame.id,
          el,
          this._previewDoc?.id,
          backgroundColor
        );
      }
    }
  }

  @property({ attribute: false })
  accessor std!: BlockStdScope;

  @state()
  private accessor _isVisible = false;

  @state()
  accessor fillScreen = false;

  @property({ attribute: false })
  accessor frame!: FrameBlockModel;

  @state()
  accessor frameViewportWH = {
    width: 0,
    height: 0,
  };

  @query('editor-host')
  accessor previewEditor: EditorHost | null = null;

  @property({ attribute: false })
  accessor surfaceHeight: number = DEFAULT_PREVIEW_CONTAINER_HEIGHT;

  @property({ attribute: false })
  accessor surfaceWidth: number = DEFAULT_PREVIEW_CONTAINER_WIDTH;
}
